import { useAppSelector } from "app/hooks";
import { Panel } from "components/Panel/Panel";
import { StatTable } from "components/StatsTable/StatTable";
import { StatTableColumn } from "components/StatsTable/StatTableColumn";
import { getTourLeaderboardTournamentIds, Tour } from "model/Tour/Tour";
import {
  calculateTourDriverRecords,
  TourDriverRecordWithDisplay,
} from "model/Tour/TourDriverRecord";
import { GrowColumn } from "pages/styles";
import {
  selectDriverNameFromTournaments,
  selectDriverOrgDetailsFromTournaments,
} from "selectors/Roster";
import {
  selectTournamentName,
  selectTournamentsDriverIds,
  selectTournamentTrigram,
} from "selectors/Tournament";
import { anyStringContains, nonUndefined } from "Utility";

interface Props {
  tour: Tour;
}

// todo - cleanup resolution of names etc.
export const TourLeaderboardTab = ({ tour }: Props) => {
  const tournamentIds = getTourLeaderboardTournamentIds(tour);
  const tournamentNames = useAppSelector((state) =>
    tournamentIds.map((id) => ({
      id,
      name: selectTournamentName(state, id),
      trigram: selectTournamentTrigram(state, id),
    }))
  );

  // todo - make work with rosters depending on if tour points are driver / team oriented
  const driverIds = useAppSelector((state) =>
    selectTournamentsDriverIds(state, tournamentIds)
  );

  const records = calculateTourDriverRecords(driverIds, tour.points);
  const recordsWithDisplay = useAppSelector((state) =>
    records
      .map((r) => {
        const { id: orgId, name: orgName } =
          selectDriverOrgDetailsFromTournaments(
            state,
            r.driverId,
            tournamentIds
          ) || {};
        return {
          record: r,
          orgId,
          orgName,
          displayName:
            selectDriverNameFromTournaments(state, r.driverId, tournamentIds) ??
            "???",
        };
      })
      .sort((a, b) => {
        if (a.record.position === b.record.position) {
          return a.displayName.localeCompare(b.displayName);
        }

        return (a.record.position ?? 9999) - (b.record.position ?? 9999);
      })
  );

  const columns: StatTableColumn<TourDriverRecordWithDisplay>[] = [
    {
      type: "Position",
      positionGetter: (arg) => arg.record.position,
      sortable: true,
    },
    {
      type: "OrgLogoName",
      orgIdGetter: (arg) => arg.orgId,
      nameGetter: (arg) => arg.displayName,
      sortable: true,
    },
    {
      type: "Points",
      header: "Points",
      pointsGetter: (arg) => arg.record.totalPoints,
      sortable: true,
      sortDescFirst: true,
    },
    ...nonUndefined(
      tournamentIds.map((tournamentId) => {
        const tournamentName = tournamentNames.find(
          (t) => t.id === tournamentId
        );
        if (!tournamentName) {
          return undefined;
        }

        const column: StatTableColumn<TourDriverRecordWithDisplay> = {
          type: "Points",
          header: `${tournamentName.trigram ?? "???"}`,
          title: `${tournamentName.name ?? "???"} Points`,
          pointsGetter: (arg) => arg.record.tournamentPoints[tournamentId],
          sortable: true,
          sortDescFirst: true,
        };

        return column;
      })
    ),
  ];

  return (
    <GrowColumn>
      <Panel>
        <StatTable
          thinner={true}
          stats={recordsWithDisplay}
          columns={columns}
          keyGetter={(arg) => arg.displayName}
          paginationAmount={40}
          searchFunction={(arg, searchText) =>
            anyStringContains(searchText, arg.displayName, arg.orgName)
          }
          initialSortColumnIdx={2}
          initialSortOrder="DESC"
        />
      </Panel>
    </GrowColumn>
  );
};
